/**
 * Date and time utility functions for the applications overview.
 * Handles timestamp parsing, age calculations, and timezone concerns
 */

/**
 * Parse a timestamp string and return age in minutes
 * Handles both ISO 8601 timestamps and PostgreSQL timestamps
 * Accounts for timezone issues where DB timestamps may not include timezone info
 *
 * @param timestamp - ISO 8601 or PostgreSQL timestamp string
 * @returns Age in minutes from now, or undefined if timestamp is invalid
 */
export function getAgeInMinutes(
  timestamp: string | undefined
): number | undefined {
  if (!timestamp) return undefined;

  try {
    // Handle PostgreSQL timestamps that might have space instead of T
    let isoTimestamp = timestamp.replace(" ", "T");

    // If timestamp doesn't end with Z and doesn't have a timezone offset, treat it as UTC
    if (
      !isoTimestamp.endsWith("Z") &&
      !isoTimestamp.match(/[+-]\d{2}:\d{2}$/)
    ) {
      isoTimestamp += "Z";
    }

    const date = new Date(isoTimestamp);

    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.warn(`Invalid timestamp: ${timestamp}`);
      return undefined;
    }

    // Calculate age in minutes, accounting for browser's local timezone
    const ageMs = Date.now() - date.getTime();
    return Math.floor(ageMs / 1000 / 60);
  } catch (error) {
    console.error(`Error parsing timestamp ${timestamp}:`, error);
    return undefined;
  }
}

/**
 * Format a duration in minutes into a human-readable string
 * Examples: "5 mins", "2 hrs", "3 days"
 *
 * @param minutes - Duration in minutes
 * @returns Formatted duration string
 */
export function formatDuration(minutes: number): string {
  if (minutes < 60) {
    return `${minutes} min${minutes !== 1 ? "s" : ""}`;
  }

  const hours = minutes / 60;
  if (hours < 24) {
    const wholeHours = Math.floor(hours);
    return `${wholeHours} hr${wholeHours !== 1 ? "s" : ""}`;
  }

  const days = hours / 24;
  if (days < 7) {
    const wholeDays = Math.floor(days);
    return `${wholeDays} day${wholeDays !== 1 ? "s" : ""}`;
  }

  const weeks = days / 7;
  const wholeWeeks = Math.floor(weeks);
  return `${wholeWeeks} week${wholeWeeks !== 1 ? "s" : ""}`;
}

/**
 * Combined helper: Get age from timestamp and format it
 *
 * @param timestamp - ISO 8601 or PostgreSQL timestamp string
 * @returns Formatted age string, or undefined if timestamp is invalid
 */
export function getFormattedAge(
  timestamp: string | undefined
): string | undefined {
  const ageMinutes = getAgeInMinutes(timestamp);
  if (ageMinutes === undefined) return undefined;
  return formatDuration(ageMinutes);
}

/**
 * Format a timestamp for display (localized)
 *
 * @param timestamp - ISO 8601 or PostgreSQL timestamp string
 * @returns Formatted date string
 */
export function formatTimestamp(
  timestamp: string | undefined
): string | undefined {
  if (!timestamp) return undefined;

  try {
    // Handle PostgreSQL timestamps that might have space instead of T
    let isoTimestamp = timestamp.replace(" ", "T");

    // If timestamp doesn't end with Z and doesn't have a timezone offset, treat it as UTC
    if (
      !isoTimestamp.endsWith("Z") &&
      !isoTimestamp.match(/[+-]\d{2}:\d{2}$/)
    ) {
      isoTimestamp += "Z";
    }

    const date = new Date(isoTimestamp);

    if (isNaN(date.getTime())) {
      console.warn(`Invalid timestamp: ${timestamp}`);
      return undefined;
    }

    return date.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZoneName: "short",
    });
  } catch (error) {
    console.error(`Error formatting timestamp ${timestamp}:`, error);
    return undefined;
  }
}
